import { Col, Row } from 'antd';
import { setNestedObjectValues, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Notification from 'components/ui/base/notification/Notification';
import { H5 } from 'components/ui/base/typography';
import useCodelists from 'hooks/useCodelists';
import useFormUtils from 'hooks/useFormUtils';
import useGetDeclarationMapValues from 'hooks/useGetDeclarationMapValues';
import useItemFieldTemplates from 'hooks/useItemFieldTemplates';
import { saveItemFieldTemplateNotifs } from 'utils/notificationMessages';
import { ProductTemplateContext } from 'utils/ProductTemplateContext';
import { ItemFieldTemplate, ItemFieldTemplateContent } from 'store/item-field-templates/itemFieldTemplate';
import { DeclarationTypes } from 'store/declarations/enums/common/declaration-types';
import validate, { FormModel, transformErrorsForFormik } from 'views/declarations/uk/export/validations/validations';
import { DeclarationContextProvider } from 'views/declarations/common/DeclarationContext';
import { getDeclarationTypeByTemplate } from 'views/declarations/utils/declaration-utils';
import DeclarationFormContent from 'views/declarations/declaration-tabs/declaration-form/DeclarationFormContent';
import { StyledButton } from 'views/declarations/declaration-tabs/declaration-form/Form.styles';
import UkProductsSection from 'views/declarations/uk/UkProductsSection';
import { StyledHeader, StyledLayout } from 'views/custom-declaration/products/Products.styles';
import { ItemFieldTemplateLocationState } from './CreateItemFieldTemplateDrawer';
import getItemFieldTemplateTitle from './getItemFieldTemplateTitle';
import getItemFieldTemplateValidation from './getItemFieldTemplateValidation';
import toItemFieldTemplateRequest from './toItemFieldTemplateRequest';
import useBreadcrumb from 'hooks/useBreadcrumb';

const ITEM_FIELD_TEMPLATES_TAB_PATH = '/templates?tab=item-field';

// Format/length errors show on touched fields and block Save. Required rules are stripped,
// as an item field template may be partial.
const AddEditItemFieldTemplate: FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { country, internalType, type, id } = useParams<{
        country: string;
        internalType: string;
        type: string;
        id: string;
    }>();
    const { t } = useTranslation('common');
    const { getCodeLists } = useCodelists();
    const { setBreadcrumbRoutes } = useBreadcrumb();
    const { externalEntity, internalType: declarationInternalType } = useFormUtils();
    const transform = useGetDeclarationMapValues()?.transformData;
    const { getTemplate, createTemplate, editTemplate } = useItemFieldTemplates();

    // Set by the New Template drawer; missing when the page is opened directly or on an existing template
    const { name: newName, teamId: newTeamId } = (location.state as ItemFieldTemplateLocationState | null) ?? {};

    const [loadedTemplate, setLoadedTemplate] = useState<ItemFieldTemplate>();
    const name = loadedTemplate?.name ?? newName;
    const teamId = loadedTemplate?.teamId ?? newTeamId;

    useEffect(() => {
        const declarationType = getDeclarationTypeByTemplate(country?.toUpperCase(), internalType);
        getCodeLists(country as string, declarationType as DeclarationTypes, undefined);
    }, [country, internalType, getCodeLists]);

    useEffect(() => {
        if (!id) return;

        getTemplate(id)
            .then(setLoadedTemplate)
            .catch(() => {
                const { error } = saveItemFieldTemplateNotifs(t);
                Notification({
                    type: 'error',
                    messageTitle: error.title,
                    description: error.description,
                    placement: 'top',
                });
                navigate(ITEM_FIELD_TEMPLATES_TAB_PATH);
            });
    }, [id, getTemplate, navigate, t]);

    useEffect(() => {
        setBreadcrumbRoutes([
            {
                breadcrumbName: 'Templates',
                path: '/templates',
            },
            {
                breadcrumbName: 'Item Field Template Builder',
                path: '',
            },
        ]);
    }, [setBreadcrumbRoutes]);

    const initialValues = useMemo(
        () => (loadedTemplate ? transform?.product?.forClient?.(loadedTemplate.template, true) ?? {} : {}),
        [loadedTemplate, transform]
    );

    const validationSchema = useMemo(
        () => getItemFieldTemplateValidation(country, internalType, type),
        [country, internalType, type]
    );

    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        validateOnChange: false,
        validate:
            validationSchema &&
            (async (values) => transformErrorsForFormik(await validate(new FormModel(values), validationSchema))),
        onSubmit: () => {},
    });

    const handleSave = async () => {
        const errors = await formik.validateForm();
        const hasErrors = Object.keys(errors).length > 0;

        if (hasErrors) {
            formik.setTouched(setNestedObjectValues(errors, true), false);
            return;
        }

        const template: ItemFieldTemplateContent = transform?.product?.forServer?.(
            formik.values,
            true
        )?.governmentAgencyGoodsItem;

        const request = toItemFieldTemplateRequest({
            name: name as string,
            teamId,
            template,
            loadedTemplate,
            route: { externalEntity, internalType: declarationInternalType, type },
        });
        if (!request) {
            const { error } = saveItemFieldTemplateNotifs(t);
            Notification({
                type: 'error',
                messageTitle: error.title,
                description: error.description,
                placement: 'top',
            });
            return;
        }

        const savedTemplate = id ? await editTemplate(id, request) : await createTemplate(request);

        if (savedTemplate) navigate(ITEM_FIELD_TEMPLATES_TAB_PATH);
    };

    const title = getItemFieldTemplateTitle({ name, country, internalType, type });

    return (
        <ProductTemplateContext.Provider value={{ inProductTemplate: true }}>
            <StyledLayout style={{ height: '100%' }}>
                <StyledHeader style={{ height: 'auto' }}>
                    <Row gutter={10} style={{ marginBottom: '1.8rem' }} justify="space-between" align="top">
                        <Col>
                            <H5 style={{ fontSize: '24px' }}>{title}</H5>
                        </Col>
                        <Col style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <StyledButton onClick={() => navigate(ITEM_FIELD_TEMPLATES_TAB_PATH)}>Cancel</StyledButton>
                            <StyledButton type="primary" primary disabled={!name} onClick={handleSave}>
                                Save
                            </StyledButton>
                        </Col>
                    </Row>
                </StyledHeader>
                <DeclarationContextProvider>
                    <DeclarationFormContent showNavigation>
                        <UkProductsSection formik={formik} />
                    </DeclarationFormContent>
                </DeclarationContextProvider>
            </StyledLayout>
        </ProductTemplateContext.Provider>
    );
};

export default AddEditItemFieldTemplate;
